import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { useLocation } from "react-router-dom";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Badge,
  Input,
  Label,
  FormGroup,
  Col,
  Card,
  CardBody,
  CardHeader,
} from "reactstrap";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import moment from "moment";

function ListadoTickets() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedProjectId = queryParams.get('projectId');

  const URL_SUPPORT_TICKETS = process.env.REACT_APP_URL_SUPPORT_TICKETS;
  const URL_SUPPORT_PROJECTS = process.env.REACT_APP_URL_SUPPORT_PROJECTS;

  const [tickets, setTickets] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // Filtros
  const [filterProject, setFilterProject] = useState(preselectedProjectId || "");
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterPriority, setFilterPriority] = useState("");
  const [filterDesde, setFilterDesde] = useState("");
  const [filterHasta, setFilterHasta] = useState("");

  // Pagination
  const [comments, setComments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 20;

  const headers = [
    { name: "Cliente", field: "clientName", sortable: true },
    { name: "Último Mensaje", field: "lastMessage", sortable: false },
    { name: "Categoría", field: "category", sortable: true },
    { name: "Prioridad", field: "priority", sortable: true },
    { name: "Status", field: "status", sortable: true },
    { name: "Atendido por", field: "handledBy", sortable: true },
    { name: "Creado", field: "createdAt", sortable: true },
    { name: "Última Actividad", field: "updatedAt", sortable: true },
    { name: "Acciones", field: "actions", sortable: false },
  ];

  useEffect(() => {
    getProyectos();
  }, []);

  useEffect(() => {
    getTickets();
  }, [filterProject, filterStatus, filterPriority, filterDesde, filterHasta]);

  const getProyectos = () => {
    axios
      .get(URL_SUPPORT_PROJECTS, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setProyectos(response.data);
      })
      .catch((err) => console.log(err));
  };

  const getTickets = () => {
    showLoader();
    
    const params = new URLSearchParams();
    if (filterProject) params.append('projectId', filterProject);
    if (filterStatus.length > 0) {
      filterStatus.forEach(status => params.append('status', status));
    }
    if (filterPriority) params.append('priority', filterPriority);
    if (filterDesde) params.append('desde', filterDesde);
    if (filterHasta) params.append('hasta', filterHasta);

    axios
      .get(`${URL_SUPPORT_TICKETS}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setTickets(response.data);
        setComments(response.data);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  };

  const toggleStatusFilter = (status) => {
    if (filterStatus.includes(status)) {
      setFilterStatus(filterStatus.filter(s => s !== status));
    } else {
      setFilterStatus([...filterStatus, status]);
    }
  };

  const clearFilters = () => {
    setFilterProject("");
    setFilterStatus([]);
    setFilterPriority("");
    setFilterDesde("");
    setFilterHasta("");
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: "primary",
      ai_handling: "success",
      ai_paused: "warning",
      team_handling: "info",
      waiting_client: "secondary",
      resolved: "secondary",
      closed: "dark",
    };
    return colors[status] || "secondary";
  };

  const getPriorityBadgeColor = (priority) => {
    const colors = {
      low: "secondary",
      medium: "info",
      high: "warning",
      critical: "danger",
    };
    return colors[priority] || "secondary";
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      bug: "danger",
      feature: "success",
      question: "info",
      data_fix: "warning",
      access: "primary",
      urgent: "danger",
      other: "secondary",
    };
    return colors[category] || "secondary";
  };

  const getLastMessage = (ticket) => {
    if (ticket.messages && ticket.messages.length > 0) {
      const lastMsg = ticket.messages[ticket.messages.length - 1];
      const preview = lastMsg.content.substring(0, 50);
      return preview + (lastMsg.content.length > 50 ? "..." : "");
    }
    return "Sin mensajes";
  };

  const commentsData = useMemo(() => {
    let computedComments = comments;

    if (search) {
      computedComments = computedComments.filter(
        (ticket) =>
          ticket.clientName?.toLowerCase().includes(search.toLowerCase()) ||
          ticket.category?.toLowerCase().includes(search.toLowerCase()) ||
          ticket.status?.toLowerCase().includes(search.toLowerCase())
      );
    }

    setTotalItems(computedComments.length);

    if (sorting.field) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computedComments = computedComments.sort((a, b) => {
        if (sorting.field === "createdAt" || sorting.field === "updatedAt") {
          return reversed * (new Date(a[sorting.field]) - new Date(b[sorting.field]));
        }
        return reversed * String(a[sorting.field]).localeCompare(String(b[sorting.field]));
      });
    }

    return computedComments.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting]);

  return (
    <>
      <Header />
      {loader}
      {user.menu_soporte ? (
        <div className="container-fluid">
          <br />
          <Row>
            <div className="col-md-12">
              <Card>
                <CardHeader>
                  <h3>Tickets de Soporte</h3>
                </CardHeader>
                <CardBody>
                  {/* Filtros */}
                  <Card className="mb-3">
                    <CardBody>
                      <h5>Filtros</h5>
                      <Row>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Proyecto/Cliente</Label>
                            <Input
                              type="select"
                              value={filterProject}
                              onChange={(e) => setFilterProject(e.target.value)}
                            >
                              <option value="">Todos</option>
                              {proyectos.map((proyecto) => (
                                <option key={proyecto._id} value={proyecto._id}>
                                  {proyecto.clientName}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Prioridad</Label>
                            <Input
                              type="select"
                              value={filterPriority}
                              onChange={(e) => setFilterPriority(e.target.value)}
                            >
                              <option value="">Todas</option>
                              <option value="low">Baja</option>
                              <option value="medium">Media</option>
                              <option value="high">Alta</option>
                              <option value="critical">Crítica</option>
                            </Input>
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Desde</Label>
                            <Input
                              type="date"
                              value={filterDesde}
                              onChange={(e) => setFilterDesde(e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={3}>
                          <FormGroup>
                            <Label>Hasta</Label>
                            <Input
                              type="date"
                              value={filterHasta}
                              onChange={(e) => setFilterHasta(e.target.value)}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <Label>Status</Label>
                          <div>
                            {[
                              { value: "open", label: "Abierto" },
                              { value: "ai_handling", label: "IA Atendiendo" },
                              { value: "ai_paused", label: "IA Pausada" },
                              { value: "team_handling", label: "Equipo Atendiendo" },
                              { value: "waiting_client", label: "Esperando Cliente" },
                              { value: "resolved", label: "Resuelto" },
                              { value: "closed", label: "Cerrado" },
                            ].map(({value, label}) => (
                              <FormGroup check inline key={value}>
                                <Label check>
                                  <Input
                                    type="checkbox"
                                    checked={filterStatus.includes(value)}
                                    onChange={() => toggleStatusFilter(value)}
                                  />{" "}
                                  {label}
                                </Label>
                              </FormGroup>
                            ))}
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12} className="mt-2">
                          <Button color="secondary" size="sm" onClick={clearFilters}>
                            <i className="fas fa-times"></i> Limpiar Filtros
                          </Button>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>

                  {/* Búsqueda */}
                  <Row>
                    <div className="col-md-12 mb-3">
                      <Search
                        onSearch={(value) => {
                          setSearch(value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </Row>

                  {/* Tabla */}
                  <Table striped hover responsive>
                    <TableHeader
                      headers={headers}
                      onSorting={(field, order) => setSorting({ field, order })}
                    />
                    <tbody>
                      {commentsData.map((ticket) => (
                        <tr key={ticket._id}>
                          <td>
                            <strong>
                              {proyectos.find(p => p._id === ticket.supportProjectId)?.clientName || ticket.clientName}
                            </strong>
                          </td>
                          <td>
                            <small>{getLastMessage(ticket)}</small>
                          </td>
                          <td>
                            {ticket.category && (
                              <Badge color={getCategoryBadgeColor(ticket.category)}>
                                {ticket.category}
                              </Badge>
                            )}
                          </td>
                          <td>
                            <Badge color={getPriorityBadgeColor(ticket.priority)}>
                              {ticket.priority}
                            </Badge>
                          </td>
                          <td>
                            <Badge color={getStatusBadgeColor(ticket.status)}>
                              {ticket.status}
                            </Badge>
                          </td>
                          <td>
                            {ticket.handledBy && (
                              <Badge color={
                                ticket.handledBy === 'ai' ? 'success' :
                                ticket.handledBy === 'team' ? 'primary' : 'info'
                              }>
                                {ticket.handledBy === 'ai' ? 'IA' :
                                 ticket.handledBy === 'team' ? 'Equipo' : 'Mixto'}
                              </Badge>
                            )}
                          </td>
                          <td>
                            <small>{moment(ticket.createdAt).fromNow()}</small>
                            <br />
                            <small className="text-muted">
                              {moment(ticket.createdAt).format("DD/MM/YY HH:mm")}
                            </small>
                          </td>
                          <td>
                            <small>{moment(ticket.updatedAt).fromNow()}</small>
                          </td>
                          <td>
                            <Button
                              color="primary"
                              size="sm"
                              href={`/DetalleTicket/${ticket._id}`}
                            >
                              <i className="fas fa-eye"></i> Ver
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  {/* Paginación */}
                  <Row>
                    <div className="col-md-12">
                      <Pagination
                        total={totalItems}
                        itemsPerPage={ITEMS_PER_PAGE}
                        currentPage={currentPage}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    </div>
                  </Row>
                </CardBody>
              </Card>
            </div>
          </Row>
        </div>
      ) : undefined}
    </>
  );
}

export default ListadoTickets;
